// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import * as ShopierCommands from '@app/features/premium/commands/ShopierCommands';
import {openShopierAddTime} from '@app/features/premium/components/experience/ShopierAddTimeModal';
import {
	formatMoney,
	parseAmount,
	startShopierCheckout,
} from '@app/features/premium/components/experience/ShopierCheckoutActions';
import styles from '@app/features/premium/components/experience/ShopierPurchaseSection.module.css';
import {SHOPIER_COPY} from '@app/features/premium/utils/ShopierCopy';
import type {
	ShopierOverviewResponse,
	ShopierPlanSchema,
	ShopierPurchaseResponse,
} from '@fluxer/schema/src/domains/premium/ShopierSchemas';
import {useLingui} from '@lingui/react/macro';
import {ArrowClockwiseIcon, CheckIcon, CrownIcon, LightningIcon, SparkleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

interface ShopierPurchaseSectionProps {
	overview: ShopierOverviewResponse;
	disabled: boolean;
	disabledTooltip: React.ReactNode;
	onRefresh: () => Promise<void>;
	showHistory?: boolean;
	isPremium?: boolean;
	isLifetime?: boolean;
}

const STATUS_CLASS: Record<ShopierPurchaseResponse['status'], string> = {
	pending: styles.statusPending,
	granted: styles.statusGranted,
	review: styles.statusReview,
	refunded: styles.statusRefunded,
};

export function useShopierOverview(enabled: boolean) {
	const [overview, setOverview] = useState<ShopierOverviewResponse | null>(null);
	const refresh = useCallback(async () => {
		try {
			const next = await ShopierCommands.fetchShopierOverview();
			setOverview(next);
			if (next.purchases.some((purchase) => purchase.status === 'granted')) {
				void PremiumCommands.refreshPremiumState();
			}
		} catch {
			setOverview((current) => current ?? {enabled: false, purchasable: false, plans: [], purchases: []});
		}
	}, []);
	useEffect(() => {
		if (!enabled) return;
		void refresh();
		const onFocus = () => void refresh();
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [enabled, refresh]);
	return {overview, setOverview, refresh};
}

export function ShopierPurchaseSection({
	overview,
	disabled,
	disabledTooltip,
	onRefresh,
	showHistory = true,
	isPremium = false,
	isLifetime = false,
}: ShopierPurchaseSectionProps) {
	const {i18n} = useLingui();
	const nameArgs = {premiumProductName: PREMIUM_PRODUCT_NAME};
	const tooltip = disabled && typeof disabledTooltip === 'string' ? disabledTooltip : undefined;

	const monthly = overview.plans.find((plan) => plan.plan === 'monthly');
	const yearly = overview.plans.find((plan) => plan.plan === 'yearly');
	const monthlyAmount = parseAmount(monthly?.price ?? null);
	const yearlyAmount = parseAmount(yearly?.price ?? null);
	const savePercent =
		monthlyAmount && yearlyAmount && monthlyAmount * 12 > yearlyAmount
			? Math.round((1 - yearlyAmount / (monthlyAmount * 12)) * 100)
			: null;

	const handleSubscribe = (plan: ShopierPlanSchema) => {
		if (disabled) return;
		if (isPremium) {
			openShopierAddTime(overview.plans, plan, () => void onRefresh());
			return;
		}
		startShopierCheckout(plan, () => void onRefresh());
	};

	const features = [
		i18n._(SHOPIER_COPY.featureAllPerks, nameArgs),
		i18n._(SHOPIER_COPY.featureNoRenewal),
		i18n._(SHOPIER_COPY.featureStacks),
	];

	const cards = [monthly, yearly].filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));

	return (
		<div className={styles.root} data-flx="premium.shopier.root">
			<header className={styles.header}>
				<h2 className={styles.title}>{i18n._(SHOPIER_COPY.plansHeading)}</h2>
				<p className={styles.subtitle}>{i18n._(SHOPIER_COPY.plansSubheading)}</p>
			</header>

			<div className={styles.planGrid}>
				{cards.map((plan) => {
					const isYearly = plan.plan === 'yearly';
					const amount = isYearly ? yearlyAmount : monthlyAmount;
					return (
						<article
							key={plan.plan}
							className={clsx(styles.planCard, isYearly && styles.planCardFeatured)}
							data-flx="premium.shopier.plan-card"
						>
							{isYearly && (
								<span className={styles.ribbon}>
									<SparkleIcon weight="fill" size={12} />
									{i18n._(SHOPIER_COPY.bestValue)}
								</span>
							)}
							<div className={styles.planTop}>
								<span className={styles.planIcon}>
									{isYearly ? <CrownIcon weight="fill" size={20} /> : <LightningIcon weight="fill" size={20} />}
								</span>
								<h3 className={styles.planTitle}>
									{isYearly ? i18n._(SHOPIER_COPY.yearlyTitle) : i18n._(SHOPIER_COPY.monthlyTitle)}
								</h3>
								{isYearly && savePercent && savePercent > 0 && (
									<span className={styles.saveBadge}>{i18n._(SHOPIER_COPY.savePercent, {percent: savePercent})}</span>
								)}
							</div>
							<div className={styles.priceRow}>
								{amount ? (
									<>
										<span className={styles.price}>{formatMoney(amount, plan.currency, i18n.locale)}</span>
										<span className={styles.pricePeriod}>
											{isYearly ? i18n._(SHOPIER_COPY.perYear) : i18n._(SHOPIER_COPY.perMonth)}
										</span>
									</>
								) : (
									<span className={styles.pricePeriod}>{i18n._(SHOPIER_COPY.priceOnShopier)}</span>
								)}
							</div>
							<p className={styles.priceHint}>
								{isYearly && amount
									? i18n._(SHOPIER_COPY.monthlyEquivalent, {
											price: formatMoney(amount / 12, plan.currency, i18n.locale),
										})
									: ' '}
							</p>
							<ul className={styles.features}>
								{features.map((feature) => (
									<li key={feature}>
										<CheckIcon weight="bold" size={16} className={styles.featureIcon} />
										<span>{feature}</span>
									</li>
								))}
							</ul>
							<button
								type="button"
								className={clsx(styles.subscribe, isYearly && styles.subscribeFeatured)}
								onClick={() => handleSubscribe(plan.plan)}
								disabled={disabled || isLifetime || !overview.purchasable}
								title={tooltip}
								data-flx="premium.shopier.plan-card.subscribe"
							>
								{isLifetime
									? i18n._(SHOPIER_COPY.lifetimeMember)
									: isPremium
										? i18n._(SHOPIER_COPY.addTime)
										: i18n._(SHOPIER_COPY.subscribe)}
							</button>
						</article>
					);
				})}
			</div>

			{showHistory && overview.purchases.length > 0 && (
				<ShopierPurchaseHistory overview={overview} onRefresh={onRefresh} />
			)}
		</div>
	);
}

export function ShopierPurchaseHistory({
	overview,
	onRefresh,
	showEmpty = false,
}: {
	overview: ShopierOverviewResponse;
	onRefresh: () => Promise<void>;
	showEmpty?: boolean;
}) {
	const {i18n} = useLingui();
	const [refreshing, setRefreshing] = useState(false);
	const nameArgs = {premiumProductName: PREMIUM_PRODUCT_NAME};

	const handleRefresh = async () => {
		setRefreshing(true);
		try {
			await onRefresh();
		} finally {
			setRefreshing(false);
		}
	};

	const statusLabel = (purchase: ShopierPurchaseResponse) => {
		switch (purchase.status) {
			case 'pending':
				return i18n._(SHOPIER_COPY.statusPending);
			case 'granted':
				return purchase.item_kind
					? i18n._(SHOPIER_COPY.statusItemGranted)
					: i18n._(SHOPIER_COPY.statusGranted, nameArgs);
			case 'review':
				return i18n._(SHOPIER_COPY.statusReview);
			case 'refunded':
				return i18n._(SHOPIER_COPY.statusRefunded);
		}
	};

	if (overview.purchases.length === 0 && !showEmpty) return null;

	return (
		<section className={styles.history} data-flx="premium.shopier.history">
			<header className={styles.historyHeader}>
				<h3 className={styles.historyTitle}>{i18n._(SHOPIER_COPY.historyTitle)}</h3>
				<button
					type="button"
					className={styles.ghostButton}
					onClick={() => void handleRefresh()}
					disabled={refreshing}
					aria-busy={refreshing || undefined}
					data-flx="premium.shopier.history.refresh"
				>
					<ArrowClockwiseIcon weight="bold" size={14} className={clsx(refreshing && styles.spinning)} />
					{i18n._(SHOPIER_COPY.refresh)}
				</button>
			</header>
			{overview.purchases.length === 0 && <p className={styles.help}>{i18n._(SHOPIER_COPY.purchasesEmpty)}</p>}
			<ul className={styles.historyList}>
				{overview.purchases.map((purchase) => (
					<li key={purchase.reference_code} className={styles.historyItem}>
						<div className={styles.historyMain}>
							<span className={styles.historyPlan}>
								{purchase.item_name ??
									`${PREMIUM_PRODUCT_NAME} ${purchase.plan === 'monthly' ? i18n._(SHOPIER_COPY.monthlyTitle) : i18n._(SHOPIER_COPY.yearlyTitle)}`}
							</span>
							<code className={styles.historyCode}>{purchase.reference_code}</code>
							<span className={styles.historyDate}>
								{new Date(purchase.created_at).toLocaleDateString(i18n.locale)}
							</span>
							<span className={clsx(styles.status, STATUS_CLASS[purchase.status])}>
								{purchase.status === 'granted' && <CheckIcon weight="bold" size={12} />}
								{statusLabel(purchase)}
							</span>
						</div>
						{purchase.status === 'pending' && (
							<span className={styles.help}>{i18n._(SHOPIER_COPY.pendingExpiryHelp)}</span>
						)}
						{purchase.status === 'review' && <span className={styles.help}>{i18n._(SHOPIER_COPY.reviewHelp)}</span>}
					</li>
				))}
			</ul>
		</section>
	);
}
